/**
 * Read-only views over a ServiceStore: stats, coverage, geography rollups
 * and full-text search.
 *
 * Split from store.ts because nothing here mutates. Keeping the pure reads
 * separate makes it obvious which functions are safe to call from a render
 * path, which was not clear when they sat below 230 lines of mutation and
 * referential-integrity code.
 */

import type {
  ServiceStore, EntityKind, Service, Provider, Country, Region, City,
} from "./types"
import { displayName, pickTranslated, serviceStatusFor } from "./types"
import { countEntities, listEntities } from "./store"

// ── Queries ──────────────────────────────────────────────────────────────────

export function regionsOfCountry(store: ServiceStore, countryId: string): Region[] {
  return Object.values(store.regions)
    .filter(r => r.countryId === countryId)
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
}

export function citiesOfRegion(store: ServiceStore, regionId: string): City[] {
  return Object.values(store.cities)
    .filter(c => c.regionId === regionId)
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
}

/**
 * Providers usable for a service in `countryId`.
 *
 * Providers with no country are included rather than filtered out: a missing
 * country means "not recorded yet", not "belongs to a different country", and
 * hiding them made a service's own provider disappear from the picker.
 */
export function providersOfCountry(store: ServiceStore, countryId: string): Provider[] {
  return Object.values(store.providers)
    .filter(p => !p.countryId || p.countryId === countryId)
    // The name column is optional — it can live only in translations.
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
}

export function servicesOfProvider(store: ServiceStore, providerId: string): Service[] {
  return Object.values(store.services).filter(s => s.providerId === providerId)
}

/** Human-readable geography breadcrumb for a service. */
export function servicePath(store: ServiceStore, serviceId: string, locale: string): string {
  const s = store.services[serviceId]
  if (!s) return "(unknown)"
  const dl = store.defaultLocale
  const parts: string[] = []
  if (s.countryId && store.countries[s.countryId]) parts.push(displayName(store.countries[s.countryId], locale, dl))
  if (s.regionId && store.regions[s.regionId]) parts.push(displayName(store.regions[s.regionId], locale, dl))
  if (s.cityId && store.cities[s.cityId]) parts.push(displayName(store.cities[s.cityId], locale, dl))
  parts.push(displayName(s, locale, dl))
  return parts.join(" / ")
}

/**
 * Per-locale translation coverage across every translatable entity kind.
 *
 * `missing` counts entities with no translation at all for that locale;
 * `unreviewed` counts those translated but not yet human-checked.
 */
export function localeCoverage(store: ServiceStore, kinds?: EntityKind[]) {
  const translatable: EntityKind[] = kinds ?? [
    "country", "region", "city", "provider", "service",
    "providerCategory", "serviceCategory", "tag",
    "population", "accessibility",
  ]
  const locales = Object.keys(store.languages)
  return locales.map(locale => {
    let total = 0, translated = 0, unreviewed = 0
    const missingByKind: Record<string, number> = {}
    for (const kind of translatable) {
      for (const e of listEntities(store, kind)) {
        total++
        const t = e.translations?.[locale]
        if (t) {
          translated++
          if (!t.reviewed) unreviewed++
        } else {
          missingByKind[kind] = (missingByKind[kind] ?? 0) + 1
        }
      }
    }
    return {
      locale,
      isDefault: locale === store.defaultLocale,
      total,
      translated,
      missing: total - translated,
      unreviewed,
      percent: total ? Math.round((translated / total) * 100) : 0,
      missingByKind,
    }
  })
}

/** Entities with no translation for `locale`, so the agent can work through them. */
export function missingTranslations(
  store: ServiceStore,
  locale: string,
  opts: { kinds?: EntityKind[]; limit?: number } = {}
) {
  const kinds: EntityKind[] = opts.kinds ?? [
    "country", "region", "city", "provider", "service",
    "providerCategory", "serviceCategory", "tag",
    "population", "accessibility",
  ]
  const out: Array<{ kind: EntityKind; id: string; name: string; sourceLocale?: string }> = []
  for (const kind of kinds) {
    for (const e of listEntities(store, kind)) {
      if (e.translations?.[locale]) continue
      // Report which locale the translator should work from.
      const source = e.translations?.[store.defaultLocale]
        ? store.defaultLocale
        : Object.keys(e.translations ?? {})[0]
      out.push({
        kind,
        id: e.id,
        name: displayName(e, store.defaultLocale, store.defaultLocale),
        sourceLocale: source,
      })
    }
  }
  return out.slice(0, opts.limit ?? 100)
}

export function stats(store: ServiceStore) {
  const services = Object.values(store.services)
  return {
    services: services.length,
    // Counted for the default locale, since status now varies per locale.
    published: services.filter(s => serviceStatusFor(s, store.defaultLocale) === "published").length,
    drafts: services.filter(s => serviceStatusFor(s, store.defaultLocale) === "draft").length,
    providers: countEntities(store, "provider"),
    countries: countEntities(store, "country"),
    regions: countEntities(store, "region"),
    cities: countEntities(store, "city"),
    serviceCategories: countEntities(store, "serviceCategory"),
    tags: countEntities(store, "tag"),
    populations: countEntities(store, "population"),
    accessibility: countEntities(store, "accessibility"),
    languages: countEntities(store, "language"),
  }
}

export function stripHtml(html?: string): string {
  return (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
}

// ── Search ───────────────────────────────────────────────────────────────────

export interface ServiceSearchHit {
  kind: EntityKind
  id: string
  locale: string
  name: string
  path?: string
  score: number
  snippet: string
}

export interface ServiceSearchOptions {
  limit?: number
  kinds?: EntityKind[]
  locale?: string
  /** Services only: restrict by status. */
  status?: string
  /** Services only: restrict by geography. */
  countryId?: string
  regionId?: string
  cityId?: string
  providerId?: string
  /** Services only: must carry ALL of these category ids. */
  categoryIds?: string[]
}

const STOPWORDS = new Set([
  "the", "a", "an", "and", "or", "of", "to", "in", "for", "on", "is", "are",
  "be", "with", "how", "what", "do", "does", "it", "this", "that", "at", "by",
])

function tokenize(s: string): string[] {
  return (s.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []).filter(Boolean)
}

/**
 * Ranked keyword search across every entity.
 *
 * Same scoring approach as the KB CMS search: name matches outrank description
 * matches, whole-phrase hits score highest, and prefixes need 3+ characters so
 * short fragments don't match everything.
 */
export function searchStore(
  store: ServiceStore,
  query: string,
  opts: ServiceSearchOptions = {}
): ServiceSearchHit[] {
  const phrase = query.toLowerCase().trim()
  if (!phrase) return []
  const all = tokenize(phrase)
  const terms = all.filter(t => !STOPWORDS.has(t))
  const effective = terms.length ? terms : all
  if (!effective.length) return []

  const limit = opts.limit ?? 25
  const kinds: EntityKind[] = opts.kinds?.length
    ? opts.kinds
    : ["service", "provider", "serviceCategory", "tag", "population", "accessibility", "country", "region", "city"]

  const hits: ServiceSearchHit[] = []

  for (const kind of kinds) {
    for (const entity of listEntities(store, kind)) {
      // Service-only filters.
      if (kind === "service") {
        const s = entity as Service
        // Status is per-locale: when a locale is in scope, filter on that
        // locale's status rather than the service-wide one.
        if (opts.status) {
          const effective = opts.locale
            ? serviceStatusFor(s, opts.locale)
            : s.status
          if (effective !== opts.status) continue
        }
        if (opts.countryId && s.countryId !== opts.countryId) continue
        if (opts.regionId && s.regionId !== opts.regionId) continue
        if (opts.cityId && s.cityId !== opts.cityId) continue
        if (opts.providerId && s.providerId !== opts.providerId) continue
        if (opts.categoryIds?.length && !opts.categoryIds.every(c => s.categoryIds.includes(c))) continue
      } else {
        // status/provider/category are service-only concepts: a non-service
        // can never satisfy them, so drop it rather than returning unfiltered
        // noise alongside genuinely filtered services.
        if (opts.status || opts.providerId || opts.categoryIds?.length) continue
        // countryId, by contrast, is meaningful for the kinds that carry one.
        // Filter those; kinds with no country (taxonomy, languages) are not
        // country-scoped at all, so a country filter should not silently
        // admit them.
        if (opts.countryId) {
          if (!("countryId" in (entity as any))) continue
          if ((entity as any).countryId !== opts.countryId) continue
        }
      }

      const locales = Object.keys(entity.translations ?? {})
      // Fall back to the untranslated `name` when no translation row exists.
      const candidates: Array<{ locale: string; name: string; body: string }> =
        locales.length
          ? locales
              .filter(l => !opts.locale || l === opts.locale)
              .map(l => {
                const t = entity.translations[l]
                return {
                  locale: l,
                  name: t?.name ?? "",
                  body: [t?.description, t?.definition, t?.address].filter(Boolean).join(" "),
                }
              })
          : [{ locale: store.defaultLocale, name: entity.name ?? "", body: stripHtml(entity.description) }]

      let best: ServiceSearchHit | null = null

      for (const c of candidates) {
        const nameText = c.name.toLowerCase()
        const bodyText = stripHtml(c.body).toLowerCase()
        const nameTokens = new Set(tokenize(nameText))
        const bodyTokens = new Set(tokenize(bodyText))

        let score = 0
        const matched = new Set<string>()

        for (const term of effective) {
          if (nameTokens.has(term)) { score += 10; matched.add(term) }
          else if (bodyTokens.has(term)) { score += 2; matched.add(term) }
          else if (term.length >= 3) {
            if ([...nameTokens].some(t => t.startsWith(term))) { score += 5; matched.add(term) }
            else if ([...bodyTokens].some(t => t.startsWith(term))) { score += 1; matched.add(term) }
          }
        }
        if (!matched.size) continue

        if (effective.length > 1) {
          if (nameText.includes(phrase)) score += 15
          else if (bodyText.includes(phrase)) score += 6
        }
        score += matched.size * 2

        const anchor = effective.find(t => bodyText.includes(t))
        const snippet = anchor
          ? stripHtml(c.body).slice(Math.max(0, bodyText.indexOf(anchor) - 60), bodyText.indexOf(anchor) + 140).trim()
          : stripHtml(c.body).slice(0, 140).trim()

        const hit: ServiceSearchHit = {
          kind, id: entity.id, locale: c.locale, name: c.name || entity.name || "(untitled)",
          path: kind === "service" ? servicePath(store, entity.id, c.locale) : undefined,
          score, snippet,
        }
        if (!best || hit.score > best.score) best = hit
      }

      if (best) hits.push(best)
    }
  }

  return hits.sort((a, b) => b.score - a.score).slice(0, limit)
}
